import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import GiftCardIcon from "@/components/GiftCardIcon";
import { useGiftCards } from "@/hooks/useGiftCards";
import { useTheme } from "@/context/ThemeContext";
import { useI18n, LANGUAGES } from "@/context/I18nContext";
import { ArrowRight, ArrowUpRight, CheckCircle2, Loader2, Sun, Moon } from "lucide-react";

export const SOCIALS = [
    { label: "Facebook", href: "https://www.facebook.com/profile.php?id=61559714106644", color: "hover:bg-blue-600",
        icon: <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /> },
    { label: "TikTok", href: "https://www.tiktok.com/@gifttocash", color: "hover:bg-slate-700",
        icon: <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.16 8.16 0 0 0 4.77 1.52V6.76a4.85 4.85 0 0 1-1-.07z" /> },
    { label: "X", href: "https://x.com/gift_cashsend", color: "hover:bg-slate-700",
        icon: <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /> },
    { label: "Instagram", href: "https://www.instagram.com/gifttocash", color: "hover:bg-pink-600",
        icon: <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" /> },
    { label: "YouTube", href: "https://www.youtube.com/@GiftToCash", color: "hover:bg-red-600",
        icon: <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /> },
];

// Footer columns. `href` = normal link (page reload, used for #sections), `to` = in-app link.
const COLUMNS = [
    { title: "Sell", links: [
        { label: "Sell Gift Cards", to: "/dashboard/send" },
        { label: "All gift card rates", to: "/sell" },
        { label: "Rates Calculator", href: "/#rates" },
        { label: "Buy Gift Cards", to: "/buy-giftcard" },
        { label: "Marketplace", to: "/marketplace" },
        { label: "Rate alerts", to: "/dashboard/alerts" },
        { label: "Selling guides", to: "/blog?category=Sell%20Gift%20Cards" },
    ] },
    { title: "Payouts", links: [
        { label: "Interac e-Transfer", to: "/blog?category=Crypto%20%26%20Payouts" },
        { label: "PayPal", to: "/blog?category=Crypto%20%26%20Payouts" },
        { label: "Cash App", to: "/blog?category=Crypto%20%26%20Payouts" },
        { label: "Crypto (USDT, BTC)", to: "/blog?category=Crypto%20%26%20Payouts" },
        { label: "My wallet", to: "/dashboard/wallet" },
    ] },
    { title: "Help", links: [
        { label: "FAQ", href: "/#faq" },
        { label: "How it works", href: "/#how-it-works" },
        { label: "Check gift card balance", to: "/gift-card-balance" },
        { label: "Avoid scams", to: "/blog?category=Safety%20%26%20Scams" },
        { label: "Open a ticket", to: "/dashboard/support" },
        { label: "Contact us", to: "/contact" },
    ] },
    { title: "Company", links: [
        { label: "Reviews", to: "/reviews" },
        { label: "Refer & earn", to: "/dashboard/rewards" },
        { label: "Blog", to: "/blog" },
        { label: "Get the app", href: "/#get-app" },
        { label: "My account", to: "/dashboard" },
        { label: "Create account", to: "/register" },
    ] },
];

const LEGAL = [
    { label: "Privacy Policy", to: "/privacy-policy" },
    { label: "Terms & Conditions", to: "/terms-condition" },
    { label: "Cookie Policy", to: "/cookie-policy" },
    { label: "AML / KYC", to: "/aml-kyc-policy" },
    { label: "Acceptable Use", to: "/acceptable-use" },
];

// Where the floating card icons sit around the newsletter box (desktop only).
const SPOTS = [
    { cls: "left-[6%] top-[22%]", size: 46, delay: "0s" },
    { cls: "left-[17%] top-[58%]", size: 40, delay: "1.2s" },
    { cls: "left-[3%] bottom-[12%]", size: 34, delay: "2.1s" },
    { cls: "right-[5%] top-[12%]", size: 40, delay: ".6s" },
    { cls: "right-[17%] top-[40%]", size: 36, delay: "1.7s" },
    { cls: "right-[8%] bottom-[14%]", size: 44, delay: "2.6s" },
];
const FALLBACK_CODES = ["AMAZON", "STEAM", "PAYPAL", "XBOX", "GOOGLE_PLAY", "WALMART"];

const FLOAT_CSS = `@keyframes g2cCardFloat{0%,100%{transform:translateY(0) rotate(var(--r,0deg))}50%{transform:translateY(-10px) rotate(var(--r,0deg))}}
.g2c-float{animation:g2cCardFloat 6s ease-in-out infinite}
@media (prefers-reduced-motion:reduce){.g2c-float{animation:none}}`;

function FooterLink({ l }) {
    const cls = "text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1.5";
    const inner = (
        <>
            {l.label}
            {l.external && <ArrowUpRight size={13} className="opacity-70" />}
            {l.badge && <span className="text-[9px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded">{l.badge}</span>}
        </>
    );
    if (l.to) return <Link to={l.to} className={cls}>{inner}</Link>;
    return <a href={l.href} className={cls} {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{inner}</a>;
}

function FloatingCards() {
    const cards = useGiftCards();
    const withIcons = (cards || []).filter((c) => c.icon_url && c.is_active !== false).slice(0, SPOTS.length);
    const items = SPOTS.map((s, i) => ({ ...s, card: withIcons[i] || null, code: withIcons[i]?.code || FALLBACK_CODES[i] }));
    return (
        <div className="hidden md:block absolute inset-0 pointer-events-none" aria-hidden="true">
            {items.map((it, i) => (
                <div key={i} className={`absolute ${it.cls}`}>
                    <div className="g2c-float bg-white dark:bg-slate-800 rounded-2xl shadow-[0_12px_30px_-10px_rgba(15,23,41,.25)] p-3"
                        style={{ animationDelay: it.delay, "--r": `${i % 2 ? 6 : -6}deg` }}>
                        <GiftCardIcon code={it.code} iconUrl={it.card?.icon_url} size={it.size} />
                    </div>
                </div>
            ))}
        </div>
    );
}

function NewsletterBand() {
    const [email, setEmail] = useState("");
    const [state, setState] = useState("");      // "" | busy | done | error text
    const [count, setCount] = useState(0);
    useEffect(() => { api.get("/newsletter-count").then((r) => setCount(Number(r.data?.count) || 0)).catch(() => {}); }, []);
    const submit = async (e) => {
        e.preventDefault();
        setState("busy");
        // Clicking Subscribe next to the consent notice below is the opt-in.
        try { await api.post("/blog-subscribe", { email, consent: true }); setState("done"); setCount((c) => c + 1); }
        catch (er) { setState(er?.response?.data?.detail || "Something went wrong. Try again."); }
    };
    return (
        <section className="relative overflow-hidden bg-slate-50 dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800">
            <style>{FLOAT_CSS}</style>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[620px] h-[420px] rounded-full bg-emerald-200/40 dark:bg-emerald-500/10 blur-3xl" aria-hidden="true" />
            <FloatingCards />
            <div className="relative max-w-2xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
                <h2 className="font-heading font-extrabold text-3xl sm:text-[2.6rem] leading-tight text-slate-900 dark:text-white">
                    GiftToCash updates<br className="hidden sm:block" /> in your inbox
                </h2>
                <p className="mt-4 text-slate-500 dark:text-slate-400 text-sm sm:text-base leading-relaxed">
                    New guides, rate updates and useful reminders about selling gift cards. Unsubscribe whenever you like.
                </p>
                {state === "done" ? (
                    <div className="mt-8 inline-flex items-center gap-2 bg-white dark:bg-slate-800 border border-emerald-200 text-emerald-700 dark:text-emerald-300 font-semibold px-6 py-3.5 rounded-full shadow-sm">
                        <CheckCircle2 size={18} /> You're subscribed. Check your inbox soon!
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-8 mx-auto max-w-lg bg-white dark:bg-slate-800 rounded-full p-1.5 pl-5 flex items-center gap-2 shadow-[0_10px_30px_-12px_rgba(15,23,41,.25)] ring-1 ring-slate-200/70 dark:ring-slate-700 focus-within:ring-emerald-400">
                        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your e-mail" aria-label="Email address"
                            className="flex-1 min-w-0 bg-transparent outline-none text-sm text-slate-800 dark:text-white placeholder:text-slate-400" />
                        <button disabled={state === "busy"} className="shrink-0 inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold text-sm px-5 py-2.5 rounded-full">
                            {state === "busy" ? <Loader2 size={15} className="animate-spin" /> : null} Subscribe <ArrowRight size={15} />
                        </button>
                    </form>
                )}
                {state && state !== "busy" && state !== "done" && <p className="mt-3 text-xs text-rose-600">{state}</p>}
                <p className="mt-4 text-[11px] sm:text-xs text-slate-400">
                    By subscribing you agree to get emails from GiftToCash and to our <Link to="/privacy-policy" className="underline hover:text-slate-600">Privacy Policy</Link>.
                    {count >= 50 && <> <span className="mx-1.5">•</span>{count.toLocaleString()} subscribers</>}
                </p>
            </div>
        </section>
    );
}

function Controls() {
    const { theme, setTheme } = useTheme();
    const { lang, setLanguage } = useI18n() || {};
    const pill = (on) => `px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${on ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`;
    return (
        <div className="flex items-center gap-2 flex-wrap">
            {setLanguage && (
                <div className="flex items-center gap-0.5 bg-white/5 border border-white/10 rounded-xl p-1">
                    {LANGUAGES.map((l) => (
                        <button key={l.code} type="button" onClick={() => setLanguage(l.code)} className={pill(lang === l.code)}>{l.label}</button>
                    ))}
                </div>
            )}
            <div className="flex items-center gap-0.5 bg-white/5 border border-white/10 rounded-xl p-1">
                <button type="button" aria-label="Light theme" onClick={() => setTheme("light")} className={pill(theme !== "dark")}><Sun size={14} /></button>
                <button type="button" aria-label="Dark theme" onClick={() => setTheme("dark")} className={pill(theme === "dark")}><Moon size={14} /></button>
            </div>
        </div>
    );
}

/**
 * Site-wide footer: newsletter band with floating gift cards + dark link footer.
 * <SiteFooter />                       everything
 * <SiteFooter newsletter={false} />    links only
 * <SiteFooter extra={<FootballBuddy />} />  something extra under the logo column
 */
export default function SiteFooter({ newsletter = true, extra = null }) {
    return (
        <>
            {newsletter && <NewsletterBand />}
            <footer className="bg-navy text-slate-400">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8">
                    <div className="grid gap-10 lg:grid-cols-[1.35fr_2fr] pb-10 border-b border-white/10">
                        <div>
                            <Link to="/" aria-label="GiftToCash home"><Logo className="h-10" variant="dark" testId="footer-logo" /></Link>
                            <p className="mt-5 text-sm leading-relaxed max-w-md">
                                GiftToCash turns unused gift cards into money. Check the rate in the calculator, send your card and get paid to your
                                wallet after the check. Withdraw by Interac e-Transfer, PayPal, Cash App or crypto, with clear rates for every brand
                                and a support team ready to help, for people in Canada and the USA.
                            </p>
                            <div className="mt-5 flex items-center gap-2 flex-wrap">
                                {SOCIALS.map((s) => (
                                    <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                                        className={`w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-slate-300 hover:text-white transition-colors ${s.color}`}>
                                        <svg width="17" height="17" fill="currentColor" viewBox="0 0 24 24">{s.icon}</svg>
                                    </a>
                                ))}
                            </div>
                            <div className="mt-5"><Controls /></div>
                            {extra}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
                            {COLUMNS.map((c) => (
                                <div key={c.title}>
                                    <div className="text-white font-semibold mb-4">{c.title}</div>
                                    <ul className="space-y-2.5">
                                        {c.links.map((l) => <li key={l.label}><FooterLink l={l} /></li>)}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="pt-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4 text-xs text-slate-500">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                            <span>© {new Date().getFullYear()} GiftToCash. All rights reserved.</span>
                            {LEGAL.map((l) => <Link key={l.to} to={l.to} className="hover:text-white transition-colors">{l.label}</Link>)}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                            <a href="mailto:support@gifttocash.com" className="hover:text-white transition-colors">support@gifttocash.com</a>
                            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> All systems operational</span>
                        </div>
                    </div>
                </div>
            </footer>
        </>
    );
}
